import React, { useState } from 'react';
import { toast } from 'react-toastify';
import styles from './AddContactModal.module.css';

const AddContactModal = ({ contractorId, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    job_title: '',
    phone_number: '',
    mobile_number: '',
    fax_number: '',
    department: '',
    reports_to: '',
    date_started_with_company: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Phone number validation helper
  const isValidPhoneNumber = (phone) => {
    if (!phone || !phone.trim()) return false;
    // Remove spaces, dashes, parentheses, and plus signs for validation
    const cleaned = phone.replace(/[\s\-()+]/g, '');
    // Check if it contains only digits and is between 7 and 15 digits (international standard)
    return /^\d{7,15}$/.test(cleaned);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Phone number validation (matching Laravel: required_without_all:mobile_number)
    // At least one of phone_number or mobile_number must be provided
    const hasPhoneNumber = formData.phone_number && formData.phone_number.trim();
    const hasMobileNumber = formData.mobile_number && formData.mobile_number.trim();
    
    if (!hasPhoneNumber && !hasMobileNumber) {
      newErrors.phone_number = 'Either phone number or mobile number is required';
      newErrors.mobile_number = 'Either phone number or mobile number is required';
    } else {
      // Validate phone number format if provided
      if (hasPhoneNumber && !isValidPhoneNumber(formData.phone_number)) {
        newErrors.phone_number = 'Please enter a valid phone number (7-15 digits)';
      }
      
      // Validate mobile number format if provided
      if (hasMobileNumber && !isValidPhoneNumber(formData.mobile_number)) {
        newErrors.mobile_number = 'Please enter a valid mobile number (7-15 digits)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm() && !loading) {
      setLoading(true);
      // Clear previous errors
      setErrors({});
      try {
        const contactData = {
          ...formData,
          contractor_id: contractorId,
          role_id: 3 // CONTRACTOR role ID
        };
        const result = await onSave(contactData);
        
        console.log("📋 [MODAL] onSave result:", result);
        
        // Check if there are validation errors in the response
        if (result && !result.success) {
          const newErrors = {};
          let errorMessage = null;
          
          // Map backend errors to form errors
          if (result.errors) {
            // Email errors
            if (result.errors.email) {
              const emailError = Array.isArray(result.errors.email) 
                ? result.errors.email[0] 
                : result.errors.email;
              newErrors.email = emailError;
              errorMessage = emailError;
            }
            
            // Phone number errors
            if (result.errors.phone_number) {
              const phoneError = Array.isArray(result.errors.phone_number) 
                ? result.errors.phone_number[0] 
                : result.errors.phone_number;
              newErrors.phone_number = phoneError;
            }
            
            // Mobile number errors
            if (result.errors.mobile_number) {
              const mobileError = Array.isArray(result.errors.mobile_number) 
                ? result.errors.mobile_number[0] 
                : result.errors.mobile_number;
              newErrors.mobile_number = mobileError;
            }
          }
          
          // Also check if message contains email error
          if (!newErrors.email && result.message) {
            if (result.message.toLowerCase().includes("email") || result.message.toLowerCase().includes("already registered")) {
              newErrors.email = result.message;
              errorMessage = result.message;
            } else {
              errorMessage = result.message;
            }
          }
          
          // Set errors in state
          if (Object.keys(newErrors).length > 0) {
            console.log("📋 [MODAL] Setting errors:", newErrors);
            setErrors(newErrors);
          } else if (errorMessage) {
            // If no field-specific errors but there's a message, show it as a general error
            console.log("📋 [MODAL] Setting general error:", errorMessage);
            // Show toast as fallback
            toast.error(errorMessage);
          }
          
          // Always show toast for email errors
          if (errorMessage && (errorMessage.toLowerCase().includes("email") || errorMessage.toLowerCase().includes("already registered"))) {
            toast.error(errorMessage);
          }
        } else if (result && result.success) {
          // Success - modal will be closed by parent component
          console.log("📋 [MODAL] Contact added successfully");
        } else if (!result) {
          // No result returned - show generic error
          console.log("📋 [MODAL] No result returned from onSave");
          toast.error("Failed to add contact. Please try again.");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        // Error handling is done in parent component
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <button type="button" className={styles.closeBtn} onClick={handleClose}>
            &times;
          </button>
          <h4 className={styles.modalTitle}>Add contractor contact</h4>
        </div>
        
        <div className={styles.modalBody}>
          <form onSubmit={handleSubmit}>
            <fieldset className={styles.formHorizontal}>
              <input type="hidden" name="contractor_id" value={contractorId} />
              <input type="hidden" name="role_id" value="3" />

              {/* First Name */}
              <div className={styles.formGroup}>
                <label htmlFor="first_name" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  First name:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    className={`${styles.formControl} ${errors.first_name ? styles.hasError : ''}`}
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.first_name && <span className={styles.errorMessage}>{errors.first_name}</span>}
                </div>
              </div>

              {/* Last Name */}
              <div className={styles.formGroup}>
                <label htmlFor="last_name" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  Last name:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    className={`${styles.formControl} ${errors.last_name ? styles.hasError : ''}`}
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.last_name && <span className={styles.errorMessage}>{errors.last_name}</span>}
                </div>
              </div>

              {/* Email */}
              <div className={styles.formGroup}>
                <label htmlFor="email" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  Email:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`${styles.formControl} ${errors.email ? styles.hasError : ''}`}
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.email && (
                    <span className={styles.errorMessage} style={{ display: 'block', color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                      {errors.email}
                    </span>
                  )}
                </div>
              </div>

              {/* Job Title */}
              <div className={styles.formGroup}>
                <label htmlFor="job_title" className={styles.controlLabel}>
                  Job title:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="job_title"
                    name="job_title"
                    className={styles.formControl}
                    value={formData.job_title}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div className={styles.formGroup}>
                <label htmlFor="phone_number" className={styles.controlLabel}>
                  Phone number:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    className={`${styles.formControl} ${errors.phone_number ? styles.hasError : ''}`}
                    value={formData.phone_number}
                    onChange={handleInputChange}
                  />
                  {errors.phone_number && (
                    <span className={styles.errorMessage} style={{ display: 'block', color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                      {errors.phone_number}
                    </span>
                  )}
                </div>
              </div>

              {/* Mobile Number */}
              <div className={styles.formGroup}>
                <label htmlFor="mobile_number" className={`${styles.controlLabel} ${styles.labelRequired}`}>
                  Mobile number:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="tel"
                    id="mobile_number"
                    name="mobile_number"
                    className={`${styles.formControl} ${errors.mobile_number ? styles.hasError : ''}`}
                    value={formData.mobile_number}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.mobile_number && (
                    <span className={styles.errorMessage} style={{ display: 'block', color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                      {errors.mobile_number}
                    </span>
                  )}
                </div>
              </div>

              {/* Fax Number */}
              <div className={styles.formGroup}>
                <label htmlFor="fax_number" className={styles.controlLabel}>
                  Fax number:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="tel"
                    id="fax_number"
                    name="fax_number"
                    className={styles.formControl}
                    value={formData.fax_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Department */}
              <div className={styles.formGroup}>
                <label htmlFor="department" className={styles.controlLabel}>
                  Department:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="department"
                    name="department"
                    className={styles.formControl}
                    value={formData.department}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Reports To */}
              <div className={styles.formGroup}>
                <label htmlFor="reports_to" className={styles.controlLabel}>
                  Reports to:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="reports_to"
                    name="reports_to"
                    className={styles.formControl}
                    value={formData.reports_to}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Date Started */}
              <div className={styles.formGroup}>
                <label htmlFor="date_started_with_company" className={styles.controlLabel}>
                  Date started with company:
                </label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    id="date_started_with_company"
                    name="date_started_with_company"
                    className={styles.formControl}
                    value={formData.date_started_with_company}
                    onChange={handleInputChange}
                    placeholder="dd/mm/yyyy"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className={styles.formGroup}>
                <div className={styles.controlLabel}></div>
                <div className={styles.inputGroup}>
                  <button 
                    type="submit" 
                    className={styles.btnHead}
                    disabled={loading}
                  >
                    <i className={styles.glyphiconPlus}></i> {loading ? 'Adding...' : 'Add contact'}
                  </button>
                </div>
              </div>
            </fieldset>
          </form>
        </div>
        
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnDefault} onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddContactModal; 